import { useRef, useCallback } from 'react';
import api from 'src/gql/api';

const TIMESLICE_MS = 10_000;
const CHECKPOINT_INTERVAL_MS = 300_000; // 5 minutes

const getSupportedMimeType = () => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
};

export const useCallRecorder = () => {
  const recorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const chunksBufferRef = useRef([]);
  const chunkIndexRef = useRef(0);
  const situationIdRef = useRef(null);
  const callIdRef = useRef(null);
  const mimeTypeRef = useRef('');
  const checkpointTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const isStartingRef = useRef(false);

  const uploadChunks = useCallback(
    async (chunks, situationId, callId, mimeType) => {
      if (chunks.length === 0) return;
      if (!situationId || !callId) return;

      const chunkBlob = new Blob(chunks, { type: mimeType });
      const currentIndex = chunkIndexRef.current;
      const filename = `calls/${situationId}/${callId}/chunk-${currentIndex}.webm`;

      try {
        const { data } = await api.upload.getUploadUrl({ filename });
        const uploadUrl = data?.getUploadSignedUrl?.url;
        if (!uploadUrl) throw new Error('No upload URL received');

        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': mimeType },
          mode: 'cors',
          body: chunkBlob,
        });

        await api.monitoring.addCallChunk({
          situationId,
          callId,
          chunkKey: filename,
          chunkIndex: currentIndex,
        });

        chunkIndexRef.current += 1;
      } catch (err) {
        console.error('Failed to upload call recording chunk', {
          callId,
          currentIndex,
          error: err.message,
        });
      }
    },
    []
  );

  const startRecording = useCallback(
    async ({
      situationId,
      callId,
      localStream,
      remoteStream,
      direction,
      calledNumber,
      extension,
      userId,
    }) => {
      if (!situationId || !callId) return;
      if (recorderRef.current) return; // Already recording
      if (isStartingRef.current) return; // Another call is already starting
      isStartingRef.current = true;

      situationIdRef.current = situationId;
      callIdRef.current = callId;
      chunkIndexRef.current = 0;
      chunksBufferRef.current = [];
      startTimeRef.current = Date.now();

      const mimeType = getSupportedMimeType();
      mimeTypeRef.current = mimeType;

      try {
        await api.monitoring.startCallRecording({
          situationId,
          callId,
          direction,
          calledNumber: calledNumber || null,
          extension: extension || null,
          userId,
        });
      } catch (err) {
        console.error('Failed to start call recording log', {
          callId,
          error: err.message,
        });
        isStartingRef.current = false;
        return;
      }

      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const dest = audioContext.createMediaStreamDestination();

        if (localStream) {
          audioContext.createMediaStreamSource(localStream).connect(dest);
        }
        if (remoteStream) {
          audioContext.createMediaStreamSource(remoteStream).connect(dest);
        }

        const recorderOptions = mimeType ? { mimeType } : {};
        const mediaRecorder = new MediaRecorder(dest.stream, recorderOptions);
        recorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunksBufferRef.current.push(event.data);
          }
        };

        mediaRecorder.start(TIMESLICE_MS);

        checkpointTimerRef.current = setInterval(async () => {
          const sid = situationIdRef.current;
          const cid = callIdRef.current;
          if (!sid || !cid) return;

          const pending = chunksBufferRef.current.splice(0);
          if (pending.length > 0) {
            await uploadChunks(pending, sid, cid, mimeTypeRef.current);
          }
        }, CHECKPOINT_INTERVAL_MS);
      } catch (err) {
        console.error('Failed to start MediaRecorder', {
          callId,
          error: err.message,
        });
      } finally {
        isStartingRef.current = false;
      }
    },
    [uploadChunks]
  );

  const stopRecording = useCallback(async () => {
    // ALWAYS clear the interval, even if there's no recorder
    if (checkpointTimerRef.current) {
      clearInterval(checkpointTimerRef.current);
      checkpointTimerRef.current = null;
    }

    const situationId = situationIdRef.current;
    const callId = callIdRef.current;
    const mimeType = mimeTypeRef.current;

    if (!recorderRef.current || !situationId || !callId) return;

    const recorder = recorderRef.current;
    recorderRef.current = null;

    await new Promise((resolve) => {
      recorder.onstop = resolve;
      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        resolve();
      }
    });

    const pending = chunksBufferRef.current.splice(0);
    if (pending.length > 0) {
      await uploadChunks(pending, situationId, callId, mimeType);
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    const endedAt = Date.now();
    const totalDuration = startTimeRef.current
      ? Math.round((endedAt - startTimeRef.current) / 1000)
      : 0;
    startTimeRef.current = null;

    try {
      await api.monitoring.finalizeCallRecording({
        situationId,
        callId,
        totalDuration,
      });
    } catch (err) {
      console.error('Failed to finalize call recording', {
        callId,
        error: err.message,
      });
    }

    situationIdRef.current = null;
    callIdRef.current = null;
    chunkIndexRef.current = 0;
  }, [uploadChunks]);

  const isRecording = useCallback(() => {
    return (
      recorderRef.current !== null && recorderRef.current.state === 'recording'
    );
  }, []);

  return { startRecording, stopRecording, isRecording };
};
